/**
 * NV Tabs — Consolidated Web Components for tab switching.
 *
 * Contains:
 *  - NvTabs (base class — not registered as a custom element)
 *  - NvCollectionTabs  → <nv-collection-tabs>
 *  - NvProductsTabs    → <nv-products-tabs>
 *
 * The base class handles click / keyboard navigation and the
 * setActiveTab orchestration loop.  Subclasses supply CSS selectors
 * and visual-state hooks via simple overrides.
 */

/* ================================================================
 * Base class
 * ================================================================ */
class NvTabs extends HTMLElement {
  constructor() {
    super();
    this.boundOnTabClick = this.onTabClick.bind(this);
    this.boundOnKeyDown = this.onKeyDown.bind(this);
  }

  /**
   * Subclasses override this getter to supply CSS selectors.
   * @returns {{ buttonSelector: string, panelSelector: string, dotSelector: string|null }}
   */
  get tabConfig() {
    return {
      buttonSelector: '[role="tab"]',
      panelSelector: '[role="tabpanel"]',
      dotSelector: null,
    };
  }

  connectedCallback() {
    const { buttonSelector, panelSelector, dotSelector } = this.tabConfig;

    this.buttons = this.querySelectorAll(buttonSelector);
    this.panels = this.querySelectorAll(panelSelector);
    this.dots = dotSelector ? this.querySelectorAll(dotSelector) : [];

    this.buttons.forEach((btn) => {
      btn.addEventListener('click', this.boundOnTabClick);
      btn.addEventListener('keydown', this.boundOnKeyDown);
    });

    if (this.dots.length) {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', this.boundOnTabClick);
      });
    }
  }

  disconnectedCallback() {
    this.buttons.forEach((btn) => {
      btn.removeEventListener('click', this.boundOnTabClick);
      btn.removeEventListener('keydown', this.boundOnKeyDown);
    });

    if (this.dots.length) {
      this.dots.forEach((dot) => {
        dot.removeEventListener('click', this.boundOnTabClick);
      });
    }
  }

  onTabClick(event) {
    const targetId = event.currentTarget.getAttribute('data-target');
    if (targetId) {
      this.setActiveTab(targetId);
    }
  }

  onKeyDown(event) {
    const key = event.key;
    let newIndex = -1;
    const currentIndex = Array.from(this.buttons).indexOf(event.currentTarget);

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      event.preventDefault();
      newIndex = (currentIndex + 1) % this.buttons.length;
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      event.preventDefault();
      newIndex = (currentIndex - 1 + this.buttons.length) % this.buttons.length;
    } else if (key === 'Home') {
      event.preventDefault();
      newIndex = 0;
    } else if (key === 'End') {
      event.preventDefault();
      newIndex = this.buttons.length - 1;
    }

    if (newIndex >= 0) {
      const targetButton = this.buttons[newIndex];
      const targetId = targetButton.getAttribute('data-target');
      this.setActiveTab(targetId);
      targetButton.focus();
    }
  }

  setActiveTab(targetId) {
    this.buttons.forEach((btn) => {
      const isActive = btn.getAttribute('data-target') === targetId;
      this.updateButtonState(btn, isActive);
    });

    this.panels.forEach((panel) => {
      const isActive = panel.id === targetId;
      this.updatePanelState(panel, isActive);
    });

    if (this.dots.length) {
      this.dots.forEach((dot) => {
        const isActive = dot.getAttribute('data-target') === targetId;
        this.updateDotState(dot, isActive);
      });
    }
  }

  /* ---- Hook methods — override in subclasses ---- */

  /**
   * Update a tab button's visual / ARIA state.
   * Subclasses MUST override this.
   */
  updateButtonState(btn, isActive) {
    btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
  }

  /**
   * Update a tab panel's visibility.
   * Default handles `hidden` attribute; override to add custom classes.
   */
  updatePanelState(panel, isActive) {
    if (isActive) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
  }

  /**
   * Update a navigation dot's state.
   * No-op by default — override when dots are used.
   */
  updateDotState(_dot, _isActive) {
    // no-op
  }
}

/* ================================================================
 * NvCollectionTabs  — <nv-collection-tabs>
 * Tab switching for collection / journey-stage display.
 * ================================================================ */
class NvCollectionTabs extends NvTabs {
  get tabConfig() {
    return {
      buttonSelector: '.collection-tabs__button',
      panelSelector: '.collection-tabs__panel',
      dotSelector: null,
    };
  }

  updateButtonState(btn, isActive) {
    btn.classList.toggle('collection-tabs__button--active', isActive);
    btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    btn.setAttribute('tabindex', isActive ? '0' : '-1');
  }

  updatePanelState(panel, isActive) {
    panel.classList.toggle('collection-tabs__panel--active', isActive);
    if (isActive) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
  }
}

customElements.define('nv-collection-tabs', NvCollectionTabs);

/* ================================================================
 * NvProductsTabs  — <nv-products-tabs>
 * Tab switching for bundle product display (with dot navigation).
 * ================================================================ */
class NvProductsTabs extends NvTabs {
  get tabConfig() {
    return {
      buttonSelector: '.product-tabs__button',
      panelSelector: '.product-tabs__panel',
      dotSelector: '.product-tabs__dot',
    };
  }

  updateButtonState(btn, isActive) {
    btn.classList.toggle('button--primary', isActive);
    btn.classList.toggle('button--tertiary', !isActive);
    btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
  }

  updatePanelState(panel, isActive) {
    panel.classList.toggle('product-tabs__panel--active', isActive);
    if (isActive) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
  }

  updateDotState(dot, isActive) {
    dot.classList.toggle('product-tabs__dot--active', isActive);
    dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
  }
}

customElements.define('nv-products-tabs', NvProductsTabs);
